import { Status, type ComplianceDetail, type VehicleDetail } from '../api/client'

/**
 * Renders a vehicle as plain text for WhatsApp, email or the clipboard.
 *
 * Plain text on purpose. This is read on a phone, in a chat thread, by someone
 * who may not have an account here — anything relying on markup, a font or a
 * colour would arrive as noise.
 *
 * Two rules it must not break:
 *
 *   Nothing is invented. An obligation with no date says "Unknown", never a
 *   blank and never a guess. A blank in a chat message reads as "nothing to
 *   worry about", which is the opposite of what Unknown means.
 *
 *   No storage paths, and no link that would open a document without signing in.
 *   The vehicle link points at the app, which authenticates every request.
 */
export function buildVehicleShareText(
  vehicle: VehicleDetail,
  options: { link?: string; documentCount?: number } = {},
): string {
  const lines: string[] = []

  lines.push(`*${vehicle.registration}* — vehicle compliance`)

  const identity = [
    vehicle.vehicleType,
    [vehicle.make, vehicle.model].filter(Boolean).join(' '),
    vehicle.registrationYear ? `${vehicle.registrationYear}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  if (identity) lines.push(identity)
  lines.push(`Site: ${vehicle.site ?? 'unassigned'}`)

  if (vehicle.chassisNumber) lines.push(`Chassis: ${vehicle.chassisNumber}`)
  if (vehicle.assetCode) lines.push(`Asset code: ${vehicle.assetCode}`)

  lines.push('')

  for (const c of vehicle.compliance ?? []) {
    lines.push(`${c.displayName}: ${describe(c)}`)
  }

  if (options.documentCount) {
    lines.push('')
    lines.push(
      `${options.documentCount} certificate${options.documentCount > 1 ? 's' : ''} on file.`,
    )
  }

  if (options.link) {
    lines.push('')
    lines.push(options.link)
  }

  lines.push('')
  lines.push(`As at ${today()} · Prarambh`)

  return lines.join('\n')
}

/**
 * One obligation, as a sentence.
 *
 * The overdue/remaining qualifier is included because a bare date in a chat
 * message makes the reader do the arithmetic, and that is where "16 Oct" gets
 * skimmed as fine when it was last year.
 */
function describe(c: ComplianceDetail): string {
  switch (c.status) {
    case Status.Known: {
      const date = formatDate(c.expiryDate)
      const days = c.daysUntilExpiry

      if (days == null) return date
      if (days < 0) return `${date} — EXPIRED ${age(-days)} ago`
      return `${date} — ${age(days)} left`
    }
    case Status.NoExpiry:
      return 'No expiry (lifetime tax)'
    case Status.NotApplicable:
      return 'Not applicable'
    default:
      return 'UNKNOWN — not on record'
  }
}

function age(days: number): string {
  if (days >= 730) return `${Math.round(days / 365)} years`
  if (days >= 60) return `${Math.round(days / 30)} months`
  return `${days} days`
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'

  // Parsed as local rather than through Date(iso), which treats a date-only
  // string as UTC and shifts it a day west of Greenwich.
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function today(): string {
  return new Date().toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}
